<template>
  <div>
    <PaginatedTable
      ref="table"
      table="asset-management-plans"
      :table-class="AssetManagementPlanMaster"
      :filter-definitions="filterDefinitions"
      :headers="headers"
    >
      <template #right-actions>
        <v-btn
          aria-label="Add New Asset Management Plan"
          title="Add New"
          color="secondary"
          class="mr-2"
          flat
          :disabled="!!loading"
          @click="newDialog = true"
        >
          <v-icon>mdi-plus</v-icon>New
        </v-btn>
      </template>

      <template
        #item.latest_published_timestamp="{
          item,
        }: {
          item: AssetManagementPlanMaster,
        }"
      >
        {{ item.latest_published_timestamp?.toLocaleString() ?? "N/A" }}
      </template>

      <template #item.actions="{ item }: { item: AssetManagementPlanMaster }">
        <v-tooltip location="bottom" text="Open Latest Revision">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-arrow-right"
              flat
              :disabled="!!loading"
              density="compact"
              @click="
                $router.push(
                  `/asset-management-plan?id=${item.metrics.latest_revision_id}`,
                )
              "
            />
          </template>
        </v-tooltip>
        <v-tooltip location="bottom" text="View Approval Stages">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-account-check"
              flat
              :disabled="!!loading"
              density="compact"
              @click="
                openApprovalStagesDialog(item as AssetManagementPlanMaster)
              "
            />
          </template>
        </v-tooltip>
        <v-tooltip location="bottom" text="View Revisions">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-timeline-text"
              flat
              :disabled="!!loading"
              density="compact"
              @click="openRevisionsDialog(item as AssetManagementPlanMaster)"
            />
          </template>
        </v-tooltip>
      </template>
    </PaginatedTable>
    <BaseDialog v-model="newDialog" maxWidth="500" title="New Plan">
      <v-text-field
        v-model="newPlanDetails.name"
        variant="outlined"
        density="compact"
        aria-label="Asset Managment Plan Name"
        label="Name"
      />
      <template #actions>
        <v-btn
          aria-label="Add New Asset Management Plan"
          title="Add New"
          color="secondary"
          flat
          :disabled="!newPlanDetails.name"
          :loading="!!loading"
          @click="newAssetManagementPlan()"
        >
          Add New
        </v-btn>
      </template>
    </BaseDialog>

    <BaseDialog
      v-model="approvalStagesDialog"
      max-width="600"
      title="Approval Stages"
    >
      <v-table>
        <thead>
          <tr>
            <th>Order</th>
            <th>Approvers</th>
            <th>Actions</th>
          </tr>
        </thead>
        <draggable
          v-model="currentPlanMaster.approval_stages"
          handle=".handle"
          tag="tbody"
          item-key="id"
          @change="currentPlanMaster.reorderStages()"
        >
          <template
            #item="{
              element,
            }: {
              element: AssetManagementPlanMasterApprovalStage,
            }"
          >
            <tr>
              <td>
                <v-icon class="handle" icon="mdi-drag-horizontal" />
              </td>
              <td>{{ formatApprovers(element) }}</td>
              <td>
                <v-btn
                  icon="mdi-pencil"
                  flat
                  density="compact"
                  @click="openEditApproversDialog(element)"
                />
                <v-btn
                  icon="mdi-delete"
                  flat
                  density="compact"
                  @click="deleteApprovalStage(element)"
                />
              </td>
            </tr>
          </template>
        </draggable>
      </v-table>

      <template #actions>
        <v-btn color="secondary" variant="outlined" @click="addApprovalStage">
          <v-icon>mdi-plus</v-icon>Stage
        </v-btn>
      </template>
    </BaseDialog>

    <BaseDialog
      v-model="editApproversDialog"
      max-width="400"
      title="Select Approvers"
    >
      <v-checkbox
        v-for="user in users"
        :key="user.id"
        :model-value="!!getApprover(user)"
        :label="user.name"
        density="compact"
        :disabled="disableApprovers"
        hide-details
        @update:model-value="toggleApprover(user)"
      />
    </BaseDialog>

    <BaseDialog v-model="revisionsDialog" max-width="650" title="Revisions">
      <v-table>
        <thead>
          <tr>
            <th>Revision</th>
            <th>Status</th>
            <th>Created On</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="plan in currentPlanMaster.asset_management_plans"
            :key="plan.id"
          >
            <td>{{ plan.revision }}</td>
            <td>
              <v-chip :color="plan.status_color" :text="plan.status" label />
            </td>
            <td>
              {{ plan.created.toLocaleString() }}
            </td>
            <td>
              <v-tooltip location="bottom" text="Open Revision">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-arrow-right"
                    flat
                    density="compact"
                    @click="
                      $router.push(`/asset-management-plan?id=${plan.id}`)
                    "
                  />
                </template>
              </v-tooltip>
              <v-tooltip location="bottom" text="Download PDF">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-printer-outline"
                    flat
                    density="compact"
                    :disabled="
                      plan.status !== 'Draft' && plan.status !== 'Published'
                    "
                    @click="downloadPdf(plan)"
                  />
                </template>
              </v-tooltip>
              <v-tooltip location="bottom" text="Compare Revisions">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-file-compare"
                    flat
                    density="compact"
                    @click="compareRevisionsDialog?.open(plan)"
                  />
                </template>
              </v-tooltip>
            </td>
          </tr>
        </tbody>
      </v-table>
    </BaseDialog>

    <CompareRevisionsDialog
      ref="compareRevisionsDialog"
      :master="currentPlanMaster"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from "vue";
import { useRouter } from "vue-router";

import type { FilterDefinition } from "@/types/filter";
import type { DataTableHeader } from "@/types/table";

import { AssetManagementPlanMaster, User } from "@/classes";

import { setupApiQueue } from "@/components/base/api-queue";
import {
  AssetManagementPlan,
  AssetManagementPlanMasterApprovalStage,
  AssetManagementPlanMasterApprover,
  AssetManagementPlanMasterNewDetails,
} from "@/classes/asset-management-plan";

import PaginatedTable from "@/components/base/tables/PaginatedTable.vue";
import BaseDialog from "@/components/base/dialogs/BaseDialog.vue";
import { useListStore } from "@/store/lists";
import { useConfirmStore } from "@/store/confirm";
import draggable from "vuedraggable";
import { useAuthStore } from "@/store/auth";
import axiosInstance from "@/plugins/axios";
import CompareRevisionsDialog from "@/components/asset-management-plan/diff/CompareRevisionsDialog.vue";

const $router = useRouter();
const $auth = useAuthStore();
const $confirm = useConfirmStore();
const lists = useListStore();

const users = lists.useList<User>("users");
onBeforeUnmount(() => {
  if ($auth.state.user) {
    lists.refreshList("assetManagementPlanMasters");
  }
});

const headers: DataTableHeader[] = [
  {
    title: "Name",
    key: "name",
    sortable: true,
  },
  {
    title: "Published Revision",
    key: "latest_published_revision_name",
    sortable: true,
  },
  {
    title: "Last Published Date",
    key: "latest_published_timestamp",
    sortable: true,
  },
  {
    title: "Current Revision",
    key: "latest_revision_name",
    sortable: true,
  },
  {
    title: "Current Revision Status",
    key: "latest_revision_status",
    sortable: true,
  },
  {
    title: "Action",
    key: "actions",
    sortable: false,
  },
];

const filterDefinitions: FilterDefinition[] = [
  {
    label: "Name",
    value: "name",
    type: "string",
  },
];

const table = ref<InstanceType<typeof PaginatedTable> | null>(null);

const { queueApiAction, loading } = setupApiQueue();

const newAssetManagementPlan = () => {
  queueApiAction(async () => {
    const master = await AssetManagementPlanMaster.newPlan(
      newPlanDetails.value,
    );
    newPlanDetails.value = { ...defaultDetails };
    $router.push(
      "/asset-management-plan?id=" + master.metrics.latest_revision_id,
    );
  });
};

const newDialog = ref(false);

const defaultDetails: AssetManagementPlanMasterNewDetails = { name: "" };
const newPlanDetails = ref<AssetManagementPlanMasterNewDetails>({
  ...defaultDetails,
});

const approvalStagesDialog = ref(false);
const currentPlanMaster = ref<AssetManagementPlanMaster>(
  new AssetManagementPlanMaster(),
);

const openApprovalStagesDialog = (planMaster: AssetManagementPlanMaster) => {
  approvalStagesDialog.value = true;
  currentPlanMaster.value = planMaster;
};

const addApprovalStage = () => {
  const newStage = new AssetManagementPlanMasterApprovalStage();
  newStage.order = currentPlanMaster.value.approval_stages.length;
  newStage.asset_management_plan_master_id = currentPlanMaster.value.id;
  currentPlanMaster.value.approval_stages.push(newStage);
  newStage.save();
};

const deleteApprovalStage = (stage: AssetManagementPlanMasterApprovalStage) => {
  $confirm.open({
    onConfirm: () => {
      stage.deleteObject();
      currentPlanMaster.value.approval_stages =
        currentPlanMaster.value.approval_stages.filter(
          (s) => s.id !== stage.id,
        );
      currentPlanMaster.value.approval_stages.forEach((s, i) => {
        s.order = i;
      });
    },
  });
};

const editApproversDialog = ref(false);
const currentApprovalStage = ref<AssetManagementPlanMasterApprovalStage>(
  new AssetManagementPlanMasterApprovalStage(),
);

const openEditApproversDialog = (
  stage: AssetManagementPlanMasterApprovalStage,
) => {
  editApproversDialog.value = true;
  currentApprovalStage.value = stage;
};

const formatApprovers = (stage: AssetManagementPlanMasterApprovalStage) => {
  const names: string[] = [];
  for (const approver of stage.approvers) {
    const user = users.value.find((user) => user.id === approver.user_id);
    if (user) {
      names.push(user.name);
    }
  }
  return names.join(", ");
};

const getApprover = (user: User) => {
  return currentApprovalStage.value.approvers.find(
    (approver) => approver.user_id === user.id,
  );
};

const disableApprovers = ref(false);
const toggleApprover = async (user: User) => {
  disableApprovers.value = true;
  const approver = getApprover(user);
  if (approver) {
    currentApprovalStage.value.approvers =
      currentApprovalStage.value.approvers.filter((a) => a.id !== approver.id);
    await approver.deleteObject();
  } else {
    const newApprover = new AssetManagementPlanMasterApprover();
    newApprover.user_id = user.id;
    newApprover.stage_id = currentApprovalStage.value.id;
    currentApprovalStage.value.approvers.push(newApprover);
    await newApprover.save();
  }
  disableApprovers.value = false;
};

const revisionsDialog = ref(false);
const openRevisionsDialog = (planMaster: AssetManagementPlanMaster) => {
  revisionsDialog.value = true;
  currentPlanMaster.value = planMaster;
};

const downloadPdf = async (plan: AssetManagementPlan) => {
  const data = await axiosInstance.$get(
    `/report/asset-management-plan?id=${plan.id}`,
    {
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(
    new Blob([data], { type: "application/pdf" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "asset_management_plan.pdf");
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const compareRevisionsDialog = ref<InstanceType<
  typeof CompareRevisionsDialog
> | null>(null);
</script>

<style lang="css" scoped>
.handle {
  cursor: move;
}
</style>
